import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OutputEmitterRef,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { DOCUMENT, DecimalPipe } from '@angular/common';
import { OverlayModule, type ConnectedPosition, CdkConnectedOverlay } from '@angular/cdk/overlay';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { ZagIcon } from '../icon/icon';
import { ScrollIntoViewDirective } from '../scroll-into-view/scroll-into-view.directive';
import { FormField } from '../form-field/form-field';

/** Minimum number of items in a scroll column for infinite-scroll looping to be useful. */
const MIN_LOOP_ITEMS = 8;

export interface TimeRangeValue {
  start: string | null;
  end: string | null;
}

type TimeFormat = '24h' | '12h';
type Period = 'AM' | 'PM';
type TimeMatcherContext = { selectingStart: boolean; otherValue: string | null };

export type TimeRangeSelectionMode = 'columns' | 'list';

/**
 * Apply a time mask to a raw input string.
 * Returns the masked value (digits + colons only, limited by the max digit count).
 */
function applyTimeMask(raw: string, maxDigits: number): string {
  const digits = raw.replaceAll(/\D/g, '').slice(0, maxDigits);
  let result = '';
  for (let i = 0; i < digits.length; i++) {
    if (i === 2 || i === 4) result += ':';
    result += digits[i];
  }
  return result;
}

/**
 * Return the time mask placeholder string for a given format / seconds config.
 */
function timeMaskPlaceholder(fmt: TimeFormat, showSecs: boolean): string {
  const base = showSecs ? 'HH:MM:SS' : 'HH:MM';
  return fmt === '12h' ? `${base} AM` : base;
}

/** Convert 12-hour time to 24-hour format. Returns null if hours are invalid for 12h format. */
function convertTo24Hour(hours: number, isPM: boolean): number | null {
  if (hours < 1 || hours > 12) return null;
  if (isPM && hours !== 12) return hours + 12;
  if (!isPM && hours === 12) return 0;
  return hours;
}

@Component({
  selector: 'zag-time-range-picker',
  imports: [OverlayModule, ZagIcon, DecimalPipe, TranslatePipe, ScrollIntoViewDirective],
  templateUrl: './time-range-picker.html',
  styleUrl: './time-range-picker.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimeRangePicker),
      multi: true,
    },
  ],
  host: {
    class: 'zag-time-range-picker',
    '(document:keydown.escape)': 'onEscape()',
  },
})
export class TimeRangePicker implements ControlValueAccessor, OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly hostRef = inject(ElementRef<HTMLElement>);
  private readonly document = inject(DOCUMENT);
  private readonly formField = inject(FormField, { optional: true });
  protected readonly fieldLabel = computed(() => this.formField?.label() ?? '');

  private readonly connectedOverlay = viewChild(CdkConnectedOverlay);
  private readonly startInputElement = viewChild<ElementRef<HTMLInputElement>>('startInput');
  private readonly endInputElement = viewChild<ElementRef<HTMLInputElement>>('endInput');

  private positionObserver?: ResizeObserver | null;
  private positionIntervalId?: ReturnType<typeof setInterval>;
  private lastOverlayWidth?: number;
  private skipNextStartInputBlur = false;
  private skipNextEndInputBlur = false;
  private openSnapshot?: { start: string; end: string; startText: string; endText: string };
  private emittedDuringSession = false;
  private suppressRangeSelectedReset = false;
  private readonly mobileQuery =
    typeof this.document.defaultView?.matchMedia === 'function' ? this.document.defaultView.matchMedia('(max-width: 539px)') : null;
  protected readonly isMobile = signal<boolean>(this.mobileQuery?.matches ?? false);
  private readonly onMobileChange = (e: MediaQueryListEvent) => {
    this.isMobile.set(e.matches);
    this.cdr.markForCheck();
  };

  readonly disabled = input<boolean>(false);
  readonly readonly = input<boolean>(false);
  readonly required = input<boolean>(false);
  /**
   * When true, an end time numerically earlier than the start is treated as
   * the following day (overnight shift). The end input accepts the value and
   * a "+1" badge is shown next to it.
   */
  readonly allowOvernight = input<boolean>(false);
  readonly clearable = input<boolean>(true);
  readonly format = input<TimeFormat>('24h');
  readonly showSeconds = input<boolean>(false);
  readonly selectionMode = input<TimeRangeSelectionMode>('columns');
  readonly hourStep = input<number>(1);
  readonly minuteStep = input<number>(15);
  readonly min = input<string | undefined>(undefined);
  readonly max = input<string | undefined>(undefined);
  readonly startPlaceholder = input<string>('timePicker.startTime');
  readonly endPlaceholder = input<string>('timePicker.endTime');
  readonly valueSeparator = input<string>(' - ');
  /** When true, both inputs show a time format mask (e.g. HH:MM or HH:MM:SS) */
  readonly maskInput = input<boolean>(false);
  /**
   * Optional predicate called with a candidate "HH:mm" string and selection context.
   * When it returns true the time is considered disabled: the scroll-column item is
   * greyed out and the Apply button is blocked for that value.
   *
   * `context.selectingStart` — true when the user is picking the start time.
   * `context.otherValue`     — the already-committed opposite end ("HH:mm" | null).
   *
   * Analogous to the date-picker's `disabledDateMatcher` input.
   */
  readonly disabledTimeMatcher = input<((timeStr: string, context: TimeMatcherContext) => boolean) | undefined>(undefined);

  protected readonly maskPlaceholder = computed(() => timeMaskPlaceholder(this.format(), this.showSeconds()));

  protected readonly open = signal<boolean>(false);
  protected readonly start = signal<string>('');
  protected readonly end = signal<string>('');
  protected readonly selectingStart = signal<boolean>(true);

  protected readonly currentHours = signal<number>(0);
  protected readonly currentMinutes = signal<number>(0);
  protected readonly currentSeconds = signal<number>(0);
  protected readonly currentPeriod = signal<Period>('AM');

  /** Typed text input state */
  protected readonly startInputText = signal<string>('');
  protected readonly endInputText = signal<string>('');
  protected readonly startInputError = signal<boolean>(false);
  protected readonly endInputError = signal<boolean>(false);
  protected readonly listStartDraft = signal<string>('');
  protected readonly listEndDraft = signal<string>('');
  protected readonly rangeAllSelected = signal<boolean>(false);

  /** Hours filtered by hourStep for scroll list */
  protected readonly allHours = computed(() => {
    const step = Math.max(1, this.hourStep());
    return Array.from({ length: Math.ceil(24 / step) }, (_, i) => i * step);
  });
  /** Minutes filtered by minuteStep for scroll list */
  protected readonly allMinutes = computed(() => {
    const step = Math.max(1, this.minuteStep());
    return Array.from({ length: Math.ceil(60 / step) }, (_, i) => i * step);
  });
  /** All seconds 0–59 for scroll list */
  protected readonly allSeconds: number[] = Array.from({ length: 60 }, (_, i) => i);
  /** Every hourStep × minuteStep combo as "HH:mm" for list selection mode */
  protected readonly allTimeOptions = computed(() => {
    const options: string[] = [];
    for (const h of this.allHours()) {
      for (const m of this.allMinutes()) {
        options.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
      }
    }
    return options;
  });
  /** Period options */
  protected readonly periods: Period[] = ['AM', 'PM'];

  /**
   * Looped arrays — each column renders 3 copies of the real items so the
   * user can scroll continuously. The middle copy is always the "real" one.
   * When there are too few items looping is disabled (single copy).
   */
  protected readonly loopedHours = computed(() => {
    const h = this.allHours();
    return h.length >= MIN_LOOP_ITEMS ? [...h, ...h, ...h] : h;
  });
  protected readonly isHoursLooped = computed(() => this.allHours().length >= MIN_LOOP_ITEMS);
  protected readonly loopedMinutes = computed(() => {
    const m = this.allMinutes();
    return m.length >= MIN_LOOP_ITEMS ? [...m, ...m, ...m] : m;
  });
  protected readonly isMinutesLooped = computed(() => this.allMinutes().length >= MIN_LOOP_ITEMS);
  protected readonly loopedSeconds: number[] = [...this.allSeconds, ...this.allSeconds, ...this.allSeconds];

  protected readonly overlayPositions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 4,
    },
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
      offsetY: 4,
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetY: -4,
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
      offsetY: -4,
    },
  ];

  /** On mobile the overlay is centred in the viewport via the panel class. */
  protected readonly mobileOverlayPositions: ConnectedPosition[] = [
    { originX: 'center', originY: 'center', overlayX: 'center', overlayY: 'center' },
  ];

  protected readonly showClearButton = computed(
    () => this.clearable() && (this.start().length > 0 || this.end().length > 0) && !this.disabled() && !this.readonly()
  );

  /** True when overnight mode is active and the current end is before the start. */
  protected readonly isOvernightRange = computed(
    () => this.allowOvernight() && !!this.start() && !!this.end() && this.compareTimes(this.end(), this.start()) < 0
  );

  protected readonly canApplyCurrent = computed(() => {
    const candidate = this.formatTime(this.currentHours(), this.currentMinutes(), this.currentSeconds());
    return this.isTimeAllowed(candidate);
  });
  protected readonly canConfirmListSelection = computed(() => {
    const values = this.listDraftValues();
    if (!values) return false;
    if (!this.isListTimeAllowed(this.listStartDraft(), true)) return false;
    if (!this.isListTimeAllowed(this.listEndDraft(), false)) return false;
    return this.allowOvernight() || this.compareTimes(values.start, values.end) <= 0;
  });

  /**
   * Emits when the user has committed both a start AND end time in a single
   * interaction session (either via text-input commit or overlay selection).
   * Use this for direct event binding instead of relying on valueChanges, which
   * depends on the CVA onChange chain and is unreliable in production builds.
   */
  readonly completeRange: OutputEmitterRef<TimeRangeValue> = output<TimeRangeValue>();

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: TimeRangeValue) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};

  constructor() {
    this.mobileQuery?.addEventListener('change', this.onMobileChange);

    effect(() => {
      if (this.disabled()) {
        this.open.set(false);
        this.stopPositionMonitoring();
        this.cdr.markForCheck();
      }
    });

    effect(() => {
      if (this.open() && !this.isMobile()) {
        this.startPositionMonitoring();
      } else {
        this.stopPositionMonitoring();
      }
    });
  }

  ngOnDestroy(): void {
    this.stopPositionMonitoring();
    this.mobileQuery?.removeEventListener('change', this.onMobileChange);
  }

  writeValue(value: TimeRangeValue | null): void {
    this.start.set(value?.start ?? '');
    this.end.set(value?.end ?? '');
    this.selectingStart.set(!value?.start || !!value?.end);
    this.startInputText.set(value?.start ? this.toDisplayTime(value.start) : '');
    this.endInputText.set(value?.end ? this.toDisplayTime(value.end) : '');
    this.startInputError.set(false);
    this.endInputError.set(false);
    this.loadCurrentSelection();
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: TimeRangeValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(_isDisabled: boolean): void {
    this.cdr.markForCheck();
  }

  private openDropdown(forStart: boolean): void {
    if (this.disabled() || this.readonly()) return;
    const wasOpen = this.open();
    this.selectingStart.set(forStart);
    this.loadCurrentSelection();
    if (wasOpen) {
      if (this.selectionMode() === 'columns') setTimeout(() => this.scrollColumnsToSelection(), 0);
      return;
    }
    this.openSnapshot = {
      start: this.start(),
      end: this.end(),
      startText: this.startInputText(),
      endText: this.endInputText(),
    };
    this.emittedDuringSession = false;
    if (this.selectionMode() === 'list') this.resetListDrafts();
    this.open.set(true);
    setTimeout(() => this.scrollColumnsToSelection(), 0);
  }

  protected close(): void {
    this.open.set(false);
    this.resetListDrafts();
    this.onTouched();
  }

  protected closeAndRollback(): void {
    this.rollbackToSnapshot();
    this.close();
  }

  private rollbackToSnapshot(): void {
    if (!this.openSnapshot) return;
    const snap = this.openSnapshot;
    this.start.set(snap.start);
    this.end.set(snap.end);
    this.startInputText.set(snap.startText);
    this.endInputText.set(snap.endText);
    this.resetListDrafts();
    this.startInputError.set(false);
    this.endInputError.set(false);
    this.selectingStart.set(!snap.start || !!snap.end);
    if (this.emittedDuringSession) this.emitValue();
    this.emittedDuringSession = false;
    this.openSnapshot = undefined;
  }

  protected onEscape(): void {
    if (this.open()) this.closeAndRollback();
  }

  protected clear(event: Event): void {
    event.stopPropagation();
    this.start.set('');
    this.end.set('');
    this.startInputText.set('');
    this.endInputText.set('');
    this.startInputError.set(false);
    this.endInputError.set(false);
    this.selectingStart.set(true);
    this.loadCurrentSelection();
    this.emitValue();
    this.close();
  }

  protected onStartInputChange(event: Event): void {
    this.resetRangeAllSelected();
    const target = event.target as HTMLInputElement;
    if (this.maskInput()) {
      const maxDigits = this.showSeconds() ? 6 : 4;
      const masked = applyTimeMask(target.value, maxDigits);
      this.startInputText.set(masked);
      target.value = masked;
    } else {
      this.startInputText.set(target.value);
    }
    this.startInputError.set(false);
    // Live-update the scroll columns when the typed text is already a valid time
    const parsed = this.parseUserInput(this.startInputText().trim());
    if (parsed && this.isTimeAllowed(this.formatTime(parsed.hours, parsed.minutes, parsed.seconds))) {
      this.currentHours.set(parsed.hours);
      this.currentMinutes.set(parsed.minutes);
      this.currentSeconds.set(parsed.seconds);
      this.syncCurrentPeriod();
      if (this.open()) setTimeout(() => this.scrollColumnsToSelection(), 0);
    }
  }

  protected onStartInputBlur(): void {
    this.resetRangeAllSelected();
    this.onTouched();
    if (this.skipNextStartInputBlur) {
      this.skipNextStartInputBlur = false;
      return;
    }
    this.commitStartInput();
  }

  protected onStartInputFocus(): void {
    this.resetRangeAllSelected();
    this.openDropdown(true);
    const el = this.document.activeElement as HTMLInputElement;
    if (el) setTimeout(() => el.select(), 0);
  }

  protected onStartInputKeyDown(event: KeyboardEvent): void {
    if (this.isSelectAllShortcut(event)) {
      this.applyRangeAllSelect(event, true);
      return;
    }
    if (this.rangeAllSelected() && (event.key === 'Delete' || event.key === 'Backspace')) {
      this.clearRangeFromSelectAll(event);
      return;
    }
    this.resetRangeAllSelected();
    if (event.key === 'Enter' || this.isForwardTab(event)) {
      this.commitStartInputFromKeyboard(event);
      return;
    }
    if (this.maskInput()) {
      this.handleMaskedTimeKeyDown(
        event,
        () => this.commitStartInput(),
        () => this.openDropdown(true)
      );
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      if (this.open()) this.closeAndRollback();
    } else if (event.key === 'ArrowDown' || event.key === 'F4') {
      event.preventDefault();
      if (!this.open()) this.openDropdown(true);
    }
  }

  private commitStartInput(): boolean {
    const raw = this.startInputText().trim();
    if (!raw) {
      if (this.required()) {
        this.startInputError.set(true);
        return false;
      } else {
        this.start.set('');
        this.end.set('');
        this.endInputText.set('');
        this.startInputError.set(false);
        this.syncListDraftForTypedInput(true, '');
        this.syncListDraftForTypedInput(false, '');
        this.emitValue();
        return true;
      }
    }

    const parsed = this.parseUserInput(raw);
    if (!parsed) {
      this.startInputError.set(true);
      return false;
    }

    const timeStr = this.formatTime(parsed.hours, parsed.minutes, parsed.seconds);
    if (!this.isTimeAllowed(timeStr)) {
      this.startInputError.set(true);
      return false;
    }

    // Enforce start <= end (skip when overnight mode is active — end < start is intentional)
    if (!this.allowOvernight() && this.end() && this.compareTimes(timeStr, this.end()) > 0) {
      this.startInputError.set(true);
      return false;
    }

    this.start.set(timeStr);
    this.startInputText.set(this.toDisplayTime(timeStr));
    this.startInputError.set(false);
    this.syncListDraftForTypedInput(true, timeStr);
    this.emitValue();
    return true;
  }

  protected onEndInputChange(event: Event): void {
    this.resetRangeAllSelected();
    const target = event.target as HTMLInputElement;
    if (this.maskInput()) {
      const maxDigits = this.showSeconds() ? 6 : 4;
      const masked = applyTimeMask(target.value, maxDigits);
      this.endInputText.set(masked);
      target.value = masked;
    } else {
      this.endInputText.set(target.value);
    }
    this.endInputError.set(false);
    // Live-update the scroll columns when the typed text is already a valid time
    const parsed = this.parseUserInput(this.endInputText().trim());
    if (parsed && this.isTimeAllowed(this.formatTime(parsed.hours, parsed.minutes, parsed.seconds))) {
      this.currentHours.set(parsed.hours);
      this.currentMinutes.set(parsed.minutes);
      this.currentSeconds.set(parsed.seconds);
      this.syncCurrentPeriod();
      if (this.open()) setTimeout(() => this.scrollColumnsToSelection(), 0);
    }
  }

  protected onEndInputBlur(): void {
    this.resetRangeAllSelected();
    this.onTouched();
    if (this.skipNextEndInputBlur) {
      this.skipNextEndInputBlur = false;
      return;
    }
    this.commitEndInput();
  }

  protected onEndInputFocus(): void {
    this.resetRangeAllSelected();
    this.openDropdown(false);
    const el = this.document.activeElement as HTMLInputElement;
    if (el) setTimeout(() => el.select(), 0);
  }

  protected onEndInputKeyDown(event: KeyboardEvent): void {
    if (this.isSelectAllShortcut(event)) {
      this.applyRangeAllSelect(event, false);
      return;
    }
    if (this.rangeAllSelected() && (event.key === 'Delete' || event.key === 'Backspace')) {
      this.clearRangeFromSelectAll(event);
      return;
    }
    this.resetRangeAllSelected();
    if (event.key === 'Enter') {
      this.commitEndInputOnEnter(event);
      return;
    }
    if (this.isForwardTab(event)) {
      this.commitEndInputOnTab(event);
      return;
    }
    if (this.maskInput()) {
      this.handleMaskedTimeKeyDown(
        event,
        () => this.commitEndInput(),
        () => this.openDropdown(false)
      );
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      if (this.open()) this.closeAndRollback();
    } else if (event.key === 'ArrowDown' || event.key === 'F4') {
      event.preventDefault();
      if (!this.open()) this.openDropdown(false);
    }
  }

  /** Handle keydown events in mask mode. */
  private handleMaskedTimeKeyDown(event: KeyboardEvent, onEnter: () => void, onOpen: () => void): void {
    const allowed = [
      'Backspace',
      'Delete',
      'Enter',
      'Escape',
      'Tab',
      'ArrowLeft',
      'ArrowRight',
      'ArrowDown',
      'ArrowUp',
      'F4',
      'Home',
      'End',
    ];
    if (allowed.includes(event.key)) {
      if (event.key === 'Enter') {
        event.preventDefault();
        onEnter();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        if (this.open()) this.closeAndRollback();
      } else if (event.key === 'ArrowDown' || event.key === 'F4') {
        event.preventDefault();
        if (!this.open()) onOpen();
      }
      return;
    }
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  private isSelectAllShortcut(event: KeyboardEvent): boolean {
    return (event.ctrlKey || event.metaKey) && !event.shiftKey && !event.altKey && event.key.toLowerCase() === 'a';
  }

  private applyRangeAllSelect(event: KeyboardEvent, focusedIsStart: boolean): void {
    if (!this.start() || !this.end()) return;
    const startInput = this.startInputElement()?.nativeElement;
    const endInput = this.endInputElement()?.nativeElement;
    if (!startInput || !endInput) return;
    event.preventDefault();
    const focused = focusedIsStart ? startInput : endInput;
    const other = focusedIsStart ? endInput : startInput;
    this.suppressRangeSelectedReset = true;
    focused.select();
    other.select();
    if (this.document.activeElement !== focused) focused.focus();
    this.rangeAllSelected.set(true);
    setTimeout(() => {
      this.suppressRangeSelectedReset = false;
    }, 0);
  }

  private clearRangeFromSelectAll(event: KeyboardEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.start.set('');
    this.end.set('');
    this.startInputText.set('');
    this.endInputText.set('');
    this.startInputError.set(false);
    this.endInputError.set(false);
    this.selectingStart.set(true);
    this.resetListDrafts();
    this.rangeAllSelected.set(false);
    this.emitValue();
  }

  private resetRangeAllSelected(): void {
    if (!this.suppressRangeSelectedReset && this.rangeAllSelected()) {
      this.rangeAllSelected.set(false);
    }
  }

  private commitStartInputFromKeyboard(event: KeyboardEvent): void {
    event.preventDefault();
    if (!this.commitStartInput()) {
      event.stopPropagation?.();
      return;
    }
    event.stopPropagation?.();
    this.focusEndInput();
  }

  private commitEndInputOnEnter(event: KeyboardEvent): void {
    event.preventDefault();
    if (!this.commitEndInput()) {
      event.stopPropagation?.();
      return;
    }
    this.close();
  }

  private commitEndInputOnTab(event: KeyboardEvent): void {
    if (!this.commitEndInput()) {
      event.preventDefault();
      event.stopPropagation?.();
      return;
    }
    this.skipNextEndInputBlur = true;
    this.close();
  }

  private isForwardTab(event: KeyboardEvent): boolean {
    return event.key === 'Tab' && !event.shiftKey;
  }

  private focusEndInput(): void {
    const endInput = this.endInputElement()?.nativeElement;
    if (!endInput) return;
    this.skipNextStartInputBlur = true;
    endInput.focus();
  }

  private commitEndInput(): boolean {
    const raw = this.endInputText().trim();
    if (!raw) {
      this.end.set('');
      this.endInputError.set(false);
      this.syncListDraftForTypedInput(false, '');
      this.emitValue();
      return true;
    }

    const parsed = this.parseUserInput(raw);
    if (!parsed) {
      this.endInputError.set(true);
      return false;
    }

    const timeStr = this.formatTime(parsed.hours, parsed.minutes, parsed.seconds);
    if (!this.isTimeAllowed(timeStr)) {
      this.endInputError.set(true);
      return false;
    }

    // Enforce end >= start (skip when overnight mode is active — end < start is intentional)
    if (!this.allowOvernight() && this.start() && this.compareTimes(timeStr, this.start()) < 0) {
      this.endInputError.set(true);
      return false;
    }

    this.end.set(timeStr);
    this.endInputText.set(this.toDisplayTime(timeStr));
    this.endInputError.set(false);
    this.syncListDraftForTypedInput(false, timeStr);
    this.emitValue();
    return true;
  }

  protected setSelectionTarget(selectStart: boolean): void {
    if (!selectStart && !this.start()) return;
    this.selectingStart.set(selectStart);
    this.loadCurrentSelection();
  }

  protected selectHour(h: number): void {
    if (this.disabled() || this.readonly()) return;
    this.currentHours.set(h);
    this.currentMinutes.set(0);
    this.syncCurrentPeriod();
    setTimeout(() => this.scrollColumnsToSelection(), 0);
  }

  protected selectMinute(m: number): void {
    if (this.disabled() || this.readonly()) return;
    this.currentMinutes.set(m);
  }

  protected selectSecond(s: number): void {
    if (this.disabled() || this.readonly()) return;
    this.currentSeconds.set(s);
  }

  protected selectPeriod(p: Period): void {
    if (this.disabled() || this.readonly()) return;
    if (p === this.currentPeriod()) return;
    this.currentPeriod.set(p);
    const h = this.currentHours();
    if (p === 'PM' && h < 12) this.currentHours.set(h + 12);
    else if (p === 'AM' && h >= 12) this.currentHours.set(h - 12);
  }

  protected selectTimeOption(value: string, forStart: boolean): void {
    if (this.disabled() || this.readonly()) return;
    const time = this.toListTime(value);
    if (!time || !this.isListTimeAllowed(time, forStart)) return;
    this.setListDraft(forStart, time);
  }

  protected isTimeOptionSelected(value: string, forStart: boolean): boolean {
    const time = this.toListTime(value);
    return !!time && this.listDraft(forStart) === time;
  }

  protected isTimeOptionDisabled(value: string, forStart: boolean): boolean {
    const time = this.toListTime(value);
    return !time || !this.isListTimeAllowed(time, forStart);
  }

  protected displayHourFor(h: number): string {
    if (this.format() === '12h') {
      return String(h % 12 || 12).padStart(2, '0');
    }
    return String(h).padStart(2, '0');
  }

  protected isHourSelected(h: number): boolean {
    return this.currentHours() === h;
  }

  protected isMinuteSelected(m: number): boolean {
    return this.currentMinutes() === m;
  }

  protected isSecondSelected(s: number): boolean {
    return this.currentSeconds() === s;
  }

  protected isPeriodSelected(p: Period): boolean {
    return this.currentPeriod() === p;
  }

  protected setNow(): void {
    const now = new Date();
    this.currentHours.set(now.getHours());
    this.currentMinutes.set(now.getMinutes());
    this.currentSeconds.set(now.getSeconds());
    this.syncCurrentPeriod();
    setTimeout(() => this.scrollColumnsToSelection(), 0);
  }

  protected applySelection(): void {
    if (!this.canApplyCurrent()) return;

    const selectedTime = this.formatTime(this.currentHours(), this.currentMinutes(), this.currentSeconds());

    if (this.selectingStart() || !this.start()) {
      this.start.set(selectedTime);
      this.end.set('');
      this.startInputText.set(this.toDisplayTime(selectedTime));
      this.endInputText.set('');
      this.selectingStart.set(false);
      this.emitValue();
      this.loadCurrentSelection();
      setTimeout(() => this.scrollColumnsToSelection(), 0);
      return;
    }

    if (!this.allowOvernight() && this.compareTimes(selectedTime, this.start()) < 0) {
      // Without overnight mode: swap so the earlier time becomes start
      this.end.set(this.start());
      this.endInputText.set(this.startInputText());
      this.start.set(selectedTime);
      this.startInputText.set(this.toDisplayTime(selectedTime));
    } else {
      // With overnight mode (or end >= start): keep selectedTime as end
      this.end.set(selectedTime);
      this.endInputText.set(this.toDisplayTime(selectedTime));
    }

    this.selectingStart.set(true);
    this.emitValue();
    this.close();
  }

  protected confirmListSelection(): void {
    if (!this.canConfirmListSelection()) return;
    const values = this.listDraftValues();
    if (!values) return;
    this.start.set(values.start);
    this.end.set(values.end);
    this.startInputText.set(this.toDisplayTime(values.start));
    this.endInputText.set(this.toDisplayTime(values.end));
    this.startInputError.set(false);
    this.endInputError.set(false);
    this.emitValue();
    this.close();
  }

  /**
   * Enter inside the overlay panel commits the current selection — the
   * scroll-column buttons swallow the input keydown handlers, so we listen at
   * the panel level and route to `applySelection()`.
   */
  protected onPanelKeyDown(event: KeyboardEvent): void {
    if (this.selectionMode() === 'list' || event.key !== 'Enter') return;
    if (!this.canApplyCurrent()) return;
    event.preventDefault();
    event.stopPropagation();
    this.applySelection();
  }

  private emitValue(): void {
    const value: TimeRangeValue = {
      start: this.start() || null,
      end: this.end() || null,
    };
    if (this.open()) this.emittedDuringSession = true;
    this.onChange(value);
    // Fire the output when both times are set — a reliable event-binding
    // alternative to valueChanges for consumers that stage/commit immediately.
    if (value.start !== null && value.end !== null) {
      this.completeRange.emit(value);
    }
  }

  private loadCurrentSelection(): void {
    const target = this.selectingStart() ? this.start() : this.end();
    const parsed = this.parseTime(target);
    const fallback = this.parseTime(this.min());
    const now = new Date();
    this.currentHours.set(parsed?.hours ?? fallback?.hours ?? now.getHours());
    this.currentMinutes.set(parsed?.minutes ?? fallback?.minutes ?? now.getMinutes());
    this.currentSeconds.set(parsed?.seconds ?? fallback?.seconds ?? now.getSeconds());
    this.syncCurrentPeriod();
  }

  private syncCurrentPeriod(): void {
    this.currentPeriod.set(this.currentHours() >= 12 ? 'PM' : 'AM');
  }

  private parseTime(value: string | null | undefined): { hours: number; minutes: number; seconds: number } | null {
    if (!value) return null;
    const parts = value.split(':');
    if (parts.length < 2 || parts.length > 3) return null;
    const hours = Number(parts[0]);
    const minutes = Number(parts[1]);
    const seconds = parts.length > 2 ? Number(parts[2]) : 0;
    if (
      Number.isNaN(hours) ||
      Number.isNaN(minutes) ||
      Number.isNaN(seconds) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59 ||
      seconds < 0 ||
      seconds > 59
    ) {
      return null;
    }
    return { hours, minutes, seconds };
  }

  private formatTime(hours: number, minutes: number, seconds: number): string {
    const h = String(hours).padStart(2, '0');
    const m = String(minutes).padStart(2, '0');
    if (this.showSeconds()) {
      return `${h}:${m}:${String(seconds).padStart(2, '0')}`;
    }
    return `${h}:${m}`;
  }

  private formatListTime(hours: number, minutes: number): string {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  private toListTime(value: string | null | undefined): string {
    const parsed = this.parseTime(value);
    return parsed ? this.formatListTime(parsed.hours, parsed.minutes) : '';
  }

  private listDraftValue(draft: string, committed: string): string | null {
    const parsed = this.parseTime(draft);
    if (!parsed) return null;
    const seconds = this.showSeconds() ? (this.parseTime(committed)?.seconds ?? 0) : 0;
    return this.formatTime(parsed.hours, parsed.minutes, seconds);
  }

  private listDraftValues(): { start: string; end: string } | null {
    const start = this.listDraftValue(this.listStartDraft(), this.start());
    const end = this.listDraftValue(this.listEndDraft(), this.end());
    if (!start || !end) return null;
    return { start, end };
  }

  private resetListDrafts(): void {
    this.listStartDraft.set(this.toListTime(this.start()));
    this.listEndDraft.set(this.toListTime(this.end()));
  }

  private syncListDraftForTypedInput(forStart: boolean, value: string): void {
    if (this.selectionMode() !== 'list' || !this.open()) return;
    this.setListDraft(forStart, this.toListTime(value));
  }

  private setListDraft(forStart: boolean, value: string): void {
    if (forStart) this.listStartDraft.set(value);
    else this.listEndDraft.set(value);
  }

  private listDraft(forStart: boolean): string {
    return forStart ? this.listStartDraft() : this.listEndDraft();
  }

  protected toDisplayTime(value: string): string {
    const parsed = this.parseTime(value);
    if (!parsed) return value;
    if (this.format() === '24h') return this.formatTime(parsed.hours, parsed.minutes, parsed.seconds);
    const displayHours = parsed.hours % 12 || 12;
    const period = parsed.hours >= 12 ? 'PM' : 'AM';
    const secondsPart = this.showSeconds() ? `:${String(parsed.seconds).padStart(2, '0')}` : '';
    return `${String(displayHours).padStart(2, '0')}:${String(parsed.minutes).padStart(2, '0')}${secondsPart} ${period}`;
  }

  protected toListDisplayTime(value: string): string {
    const parsed = this.parseTime(value);
    if (!parsed) return value;
    const time = this.formatListTime(parsed.hours, parsed.minutes);
    if (this.format() === '24h') return time;
    const displayHours = parsed.hours % 12 || 12;
    const period = parsed.hours >= 12 ? 'PM' : 'AM';
    return `${String(displayHours).padStart(2, '0')}:${String(parsed.minutes).padStart(2, '0')} ${period}`;
  }

  private compareTimes(first: string, second: string): number {
    const left = this.parseTime(first);
    const right = this.parseTime(second);
    if (!left || !right) return 0;
    return left.hours * 3600 + left.minutes * 60 + left.seconds - (right.hours * 3600 + right.minutes * 60 + right.seconds);
  }

  private isTimeAllowed(value: string, context: TimeMatcherContext = this.matcherContext()): boolean {
    const min = this.min();
    const max = this.max();
    if (min && this.compareTimes(value, min) < 0) return false;
    if (max && this.compareTimes(value, max) > 0) return false;
    const matcher = this.disabledTimeMatcher();
    if (!matcher) return true;
    return !matcher(value, context);
  }

  private isListTimeAllowed(value: string, selectingStart: boolean): boolean {
    const otherValue = (selectingStart ? this.listEndDraft() : this.listStartDraft()) || null;
    if (!this.isTimeAllowed(value, { selectingStart, otherValue })) return false;
    if (this.allowOvernight() || !otherValue) return true;
    const comparison = this.compareTimes(value, otherValue);
    return selectingStart ? comparison <= 0 : comparison >= 0;
  }

  private matcherContext(): TimeMatcherContext {
    const selectingStart = this.selectingStart();
    return {
      selectingStart,
      otherValue: (selectingStart ? this.end() : this.start()) || null,
    };
  }

  /**
   * True when every minute step in the given hour is blocked by `disabledTimeMatcher`.
   * Used to visually disable and prevent clicking an entire hour column item.
   * `h` may be from a looped array (> 23) — the actual hour is `h % 24`.
   */
  protected isHourDisabled(h: number): boolean {
    const matcher = this.disabledTimeMatcher();
    if (!matcher) return false;
    const hh = String(h % 24).padStart(2, '0');
    const ctx = this.matcherContext();
    return this.allMinutes().every(m => matcher(`${hh}:${String(m).padStart(2, '0')}`, ctx));
  }

  /**
   * True when the combination of `currentHours()` + the given minute is blocked.
   * `m` may be from a looped array (> 59) — the actual minute is `m % 60`.
   */
  protected isMinuteDisabled(m: number): boolean {
    const matcher = this.disabledTimeMatcher();
    if (!matcher) return false;
    const hh = String(this.currentHours()).padStart(2, '0');
    return matcher(`${hh}:${String(m % 60).padStart(2, '0')}`, this.matcherContext());
  }

  protected parseUserInput(input: string): { hours: number; minutes: number; seconds: number } | null {
    const trimmed = input.trim().toLowerCase();
    const { isPM, hasAmPm, cleanInput } = this.extractAmPm(trimmed);
    const parsed = this.parseTimeComponents(cleanInput.trim());
    if (!parsed) return null;
    const hours = hasAmPm ? convertTo24Hour(parsed.hours, isPM) : parsed.hours;
    if (hours === null) return null;
    const { minutes, seconds } = parsed;
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59 || seconds < 0 || seconds > 59) return null;
    return { hours, minutes, seconds };
  }

  private extractAmPm(trimmed: string): { isPM: boolean; hasAmPm: boolean; cleanInput: string } {
    if (trimmed.includes('pm') || trimmed.endsWith('p')) {
      return { isPM: true, hasAmPm: true, cleanInput: trimmed.replaceAll(/[ \t]*(?:pm|p)[ \t]*/gi, '') };
    }
    if (trimmed.includes('am') || trimmed.endsWith('a')) {
      return { isPM: false, hasAmPm: true, cleanInput: trimmed.replaceAll(/[ \t]*(?:am|a)[ \t]*/gi, '') };
    }
    return { isPM: false, hasAmPm: false, cleanInput: trimmed };
  }

  private parseTimeComponents(cleanInput: string): { hours: number; minutes: number; seconds: number } | null {
    if (cleanInput.includes(':')) return this.parseColonFormat(cleanInput);
    return this.parseDigitFormat(cleanInput);
  }

  private parseColonFormat(input: string): { hours: number; minutes: number; seconds: number } | null {
    const parts = input.split(':');
    if (parts.length < 2 || parts.length > 3) return null;
    const hours = Number(parts[0]);
    const minutes = Number(parts[1]);
    const seconds = parts.length > 2 ? Number(parts[2]) : 0;
    if (Number.isNaN(hours) || Number.isNaN(minutes) || Number.isNaN(seconds)) return null;
    return { hours, minutes, seconds };
  }

  private parseDigitFormat(input: string): { hours: number; minutes: number; seconds: number } | null {
    const digits = input.replaceAll(/\D/g, '');
    switch (digits.length) {
      case 1:
      case 2:
        return { hours: Number(digits), minutes: 0, seconds: 0 };
      case 3:
        return { hours: Number(digits[0]), minutes: Number(digits.slice(1)), seconds: 0 };
      case 4:
        return { hours: Number(digits.slice(0, 2)), minutes: Number(digits.slice(2)), seconds: 0 };
      case 5:
        return { hours: Number(digits[0]), minutes: Number(digits.slice(1, 3)), seconds: Number(digits.slice(3)) };
      case 6:
        return { hours: Number(digits.slice(0, 2)), minutes: Number(digits.slice(2, 4)), seconds: Number(digits.slice(4)) };
      default:
        return null;
    }
  }

  // Sync panel width on trigger resize only — per-frame updatePosition causes open flicker.
  private startPositionMonitoring(): void {
    this.stopPositionMonitoring();
    const trigger = this.hostRef.nativeElement.querySelector('.zag-time-range-picker__trigger-wrapper') as HTMLElement | null;
    if (!trigger) return;
    const syncWidth = () => {
      if (!this.open()) return;
      const overlayRef = this.connectedOverlay()?.overlayRef;
      if (!overlayRef) return;
      // Clamp to the viewport (minus the overlay viewport margin) so a wide
      // trigger can never force the panel past the window edges.
      const viewportWidth = this.document.defaultView?.innerWidth ?? Number.MAX_SAFE_INTEGER;
      const w = Math.min(Math.round(trigger.getBoundingClientRect().width), viewportWidth - 16);
      if (this.lastOverlayWidth === w) return;
      this.lastOverlayWidth = w;
      overlayRef.updateSize({ minWidth: w });
      overlayRef.updatePosition();
    };
    syncWidth();
    if (typeof ResizeObserver !== 'undefined') {
      this.positionObserver = new ResizeObserver(syncWidth);
      this.positionObserver.observe(trigger);
    } else {
      this.positionIntervalId = setInterval(syncWidth, 250);
    }
  }

  /**
   * Scroll each scroll column so the currently selected item in the **middle
   * copy** is centred. Must be called after the overlay has rendered.
   */
  private scrollColumnsToSelection(): void {
    const overlayEl = this.connectedOverlay()?.overlayRef?.overlayElement;
    if (!overlayEl) return;
    for (const column of Array.from(overlayEl.querySelectorAll<HTMLElement>('.scroll-column'))) {
      this.jumpToMiddleCopy(column);
    }
  }

  /**
   * Scroll the given column so the selected item is centred.
   * For looped columns (marked with `scroll-column--looped`) this targets
   * the middle copy; for non-looped columns it scrolls the single list.
   */
  private jumpToMiddleCopy(column: HTMLElement): void {
    const items = Array.from(column.querySelectorAll<HTMLElement>('.scroll-column__item'));
    if (items.length === 0) return;

    // Column-local centering — scrollIntoView would scroll the page and fight CDK positioning.
    const center = (item: HTMLElement | undefined) => {
      if (!item) return;
      const relativeTop = item.offsetParent === column ? item.offsetTop : item.offsetTop - column.offsetTop;
      column.scrollTop = relativeTop - column.clientHeight / 2 + item.clientHeight / 2;
    };

    const isLooped = column.classList.contains('scroll-column--looped');
    if (!isLooped) {
      const selected = items.find(el => el.getAttribute('aria-selected') === 'true');
      center(selected ?? items[0]);
      return;
    }

    const count = items.length / 3;
    for (let i = count; i < 2 * count; i++) {
      if (items[i]?.getAttribute('aria-selected') === 'true') {
        center(items[i]);
        return;
      }
    }
    center(items[count]);
  }

  /**
   * Called on every scroll event of a looped scroll column.
   * If the scroll position drifts into the first or last copy, silently
   * teleport back to the same visual position in the middle copy.
   * Non-looped columns (no `scroll-column--looped` class) are ignored.
   */
  protected onColumnScroll(event: Event): void {
    const column = event.target as HTMLElement;
    if (!column.classList.contains('scroll-column--looped')) return;
    const items = Array.from(column.querySelectorAll<HTMLElement>('.scroll-column__item'));
    if (items.length === 0) return;
    const count = items.length / 3;
    const itemHeight = column.scrollHeight / items.length;
    const copyHeight = itemHeight * count;
    const scrollTop = column.scrollTop;
    if (scrollTop < copyHeight) {
      column.scrollTop = scrollTop + copyHeight;
    } else if (scrollTop >= copyHeight * 2) {
      column.scrollTop = scrollTop - copyHeight;
    }
  }

  private stopPositionMonitoring(): void {
    this.positionObserver?.disconnect();
    this.positionObserver = undefined;
    if (this.positionIntervalId !== undefined) {
      clearInterval(this.positionIntervalId);
      this.positionIntervalId = undefined;
    }
    this.lastOverlayWidth = undefined;
  }
}
